import { LitElement, TemplateResult, html, unsafeCSS } from "lit";
import { property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
// @ts-ignore: SCSS module imported via bundler as inline
import styles from "./nys-pagination.scss?inline";

let componentIdCounter = 0;

/**
 * Page navigation with Previous/Next buttons and numbered page links. Auto-collapses with ellipses for many pages.
 *
 * Set `totalPages` and `currentPage` to control state. Listen to `nys-change` for page selection.
 * Hidden automatically when `totalPages` is 1. Responsive: shows compact controls on mobile.
 *
 * @summary Page navigation with numbered links, prev/next buttons, and responsive layout.
 * @element nys-pagination
 *
 * @fires nys-change - Fired when page changes. Detail: `{page}`.
 *
 * @example Basic pagination
 * ```html
 * <nys-pagination currentPage="1" totalPages="10"></nys-pagination>
 * ```
 */

export class NysPagination extends LitElement {
  static styles = unsafeCSS(styles);

  /** Unique identifier. Auto-generated if not provided. */
  @property({ type: String, reflect: true }) id = "";

  /** Name attribute for form association. */
  @property({ type: String, reflect: true }) name = "";

  /** Currently active page (1-indexed). Clamped to valid range. */
  @property({ type: Number, reflect: true }) currentPage = 1;

  /** Total number of pages. Must be at least 1. */
  @property({ type: Number, reflect: true }) totalPages = 1;

  /** Internal state for layout adjustments near the end. */
  @property({ type: Boolean, reflect: true }) _twoBeforeLast = false;

  /**
   * Lifecycle Methods
   * --------------------------------------------------------------------------
   */

  constructor() {
    super();
  }

  willUpdate(changedProps: Map<string, unknown>) {
    if (this.totalPages < 1) this.totalPages = 1; //ensure totalPages is at least 1

    if (changedProps.has("currentPage") || changedProps.has("totalPages")) {
      const clamped = this._clampPage(this.currentPage);
      if (clamped !== this.currentPage) {
        this.currentPage = clamped;
      }

      const twoBefore = this.currentPage === this.totalPages - 2;
      if (twoBefore !== this._twoBeforeLast) {
        this._twoBeforeLast = twoBefore;
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.id) {
      this.id = `nys-pagination-${Date.now()}-${componentIdCounter++}`;
    }
  }

  /**
   * Functions
   * --------------------------------------------------------------------------
   */

  private _clampPage(page: number): number {
    if (page < 1) return 1;
    if (page > this.totalPages) return this.totalPages;
    return page;
  }

  private renderPageButtons() {
    const buttons: TemplateResult[] = [];

    const addPageButton = (page: number, id?: string) => {
      buttons.push(html`
        <nys-button
          label=${String(page)}
          ariaLabel="Page ${page}${page === this.currentPage
            ? " - Current Page"
            : ""}"
          id=${ifDefined(id)}
          variant=${this.currentPage === page ? "filled" : "outline"}
          size="sm"
          @nys-click="${() => this._handlePageClick(page)}"
        ></nys-button>
      `);
    };

    const addSpacer = (id: string) => {
      buttons.push(
        html`<nys-button
          label="..."
          class="spacer"
          tabindex="-1"
          id=${id}
          size="sm"
        ></nys-button>`,
      );
    };

    const firstPage = 1;
    const lastPage = this.totalPages;
    const prev = this.currentPage - 1;
    const next = this.currentPage + 1;

    // Always show first page
    addPageButton(firstPage);

    // Spacer if there's a gap between first and current/prev
    if (this.currentPage > 2) {
      addSpacer("first-spacer");
    }

    // Show prev neighbor (desktop; can be hidden via CSS at mobile)
    if (prev > firstPage) {
      addPageButton(prev, "prev-page");
    }

    // Show current (only if not first/last, since they’re already rendered)
    if (this.currentPage !== firstPage && this.currentPage !== lastPage) {
      addPageButton(this.currentPage, "current-page");
    }

    // Show next neighbor (desktop; can be hidden via CSS at mobile)
    if (next < lastPage) {
      addPageButton(next, "next-page");
    }

    // Spacer if there's a gap between current/next and last
    if (this.currentPage < lastPage - 1) {
      addSpacer("last-spacer");
    }

    // Always show last page if more than one
    if (lastPage > firstPage) {
      addPageButton(lastPage);
    }

    return buttons;
  }

  /**
   * Event Handlers
   * --------------------------------------------------------------------------
   */

  private _handlePageClick(page: number) {
    this.currentPage = this._clampPage(page);
    this.dispatchEvent(
      new CustomEvent("nys-change", {
        detail: { page: this.currentPage },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    // If only one page, render nothing
    if (this.totalPages <= 1) {
      return null;
    }

    return html`<div class="nys-pagination">
      <nys-button
        id="previous"
        label="Previous"
        ariaLabel="Previous Page"
        prefixIcon="chevron_left"
        variant="outline"
        size="sm"
        ?disabled=${this.currentPage === 1}
        @nys-click="${() => this._handlePageClick(this.currentPage - 1)}"
      ></nys-button>
      <nys-button
        id="previous--mobile"
        prefixIcon="chevron_left"
        ariaLabel="Previous Page"
        variant="outline"
        size="sm"
        ?disabled=${this.currentPage === 1}
        @nys-click="${() => this._handlePageClick(this.currentPage - 1)}"
      ></nys-button>
      ${this.renderPageButtons()}
      <nys-button
        id="next"
        label="Next"
        ariaLabel="Next Page"
        suffixIcon="chevron_right"
        variant="outline"
        size="sm"
        ?disabled=${this.currentPage === this.totalPages}
        @nys-click="${() => this._handlePageClick(this.currentPage + 1)}"
      ></nys-button>
      <nys-button
        id="next--mobile"
        suffixIcon="chevron_right"
        ariaLabel="Next Page"
        variant="outline"
        size="sm"
        ?disabled=${this.currentPage === this.totalPages}
        @nys-click="${() => this._handlePageClick(this.currentPage + 1)}"
      ></nys-button>
    </div>`;
  }
  /****************** 🪡 in the Haystack Release ******/
  /****************** designsystem@its.ny.gov ********/
}

if (!customElements.get("nys-pagination")) {
  customElements.define("nys-pagination", NysPagination);
}
